export function normalizeName(value) {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

// Looser normalization for matching names that differ only by spacing/punctuation/casing
// e.g. "Mamaearth", "Mama Earth", "MAMAEARTH" -> "mamaearth"
export function normalizeLooseName(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

// Canonical display name to reduce backend duplicates when it's case-sensitive
// - trims
// - collapses whitespace
// - Title Cases words
export function canonicalizeDisplayName(value) {
  const s = String(value ?? "").trim().replace(/\s+/g, " ");
  if (!s) return "";
  return s
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");
}

export function toNumber(value) {
  if (value === null || value === undefined || value === "") return NaN;
  if (typeof value === "number") return value;
  const cleaned = String(value).replace(/,/g, "").trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

export function extractApiErrorMessage(err) {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  // axios error shape
  const status = err?.response?.status;
  const statusText = err?.response?.statusText;
  const url = err?.config?.url;
  const method = err?.config?.method;
  const msg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    err?.toString?.();

  const parts = [];
  if (status) parts.push(`HTTP ${status}${statusText ? ` ${statusText}` : ""}`);
  if (method || url) parts.push(`${String(method || "request").toUpperCase()} ${url || ""}`.trim());
  if (msg && msg !== statusText) parts.push(msg);

  return parts.filter(Boolean).join(" - ") || "Something went wrong";
}

export function computeFinalProductName({
  brandName,
  productName,
  prefixBrand,
}) {
  const b = String(brandName ?? "").trim().replace(/\s+/g, " ");
  const p = String(productName ?? "").trim().replace(/\s+/g, " ");
  if (!prefixBrand) return p;
  if (!b) return p;
  const nb = normalizeName(b);
  const np = normalizeName(p);
  if (np.startsWith(nb)) return p;
  return `${b} ${p}`.trim();
}

export function normalizeHeader(header) {
  return String(header ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

// maps common header variants (including your sample sheet) to canonical fields
export function buildHeaderMap(headers) {
  const map = {};
  (headers || []).forEach((h) => {
    const key = normalizeHeader(h);
    if (!key) return;

    // Product Name
    if (["product name", "name", "product"].includes(key)) map.productName = h;

    // Brand
    if (["brand", "brand name"].includes(key)) map.brand = h;

    // Category
    if (["category", "category name"].includes(key)) map.category = h;

    // Price
    if (
      ["price", "retail price", "mrp", "selling price", "rate"].includes(key)
    )
      map.price = h;

    // Quantity
    if (
      [
        "quantity",
        "qty",
        "quantity per product",
        "stock",
        "opening stock",
      ].includes(key)
    )
      map.quantity = h;

    // Product type
    if (["product type", "type"].includes(key)) map.productType = h;

    // Unit
    if (["unit", "uom"].includes(key)) map.unit = h;

    // HSN
    if (["hsn", "hsn code", "hsn/sac"].includes(key)) map.hsn = h;

    // Optional initial stock columns (as per your updated Excel)
    if (
      ["initial stock (retail)", "retail initial stock", "retail stock"].includes(
        key
      )
    )
      map.retailInitialStock = h;
    if (
      [
        "initial stock (store consumption)",
        "store consumption initial stock",
        "store initial stock",
        "store stock",
      ].includes(key)
    )
      map.storeInitialStock = h;
  });
  return map;
}

export function validateRow(row, headerMap) {
  const errors = [];
  const get = (field) => row?.[headerMap?.[field]];

  const productName = String(get("productName") ?? "").trim();
  const brand = String(get("brand") ?? "").trim();
  const category = String(get("category") ?? "").trim();
  const price = toNumber(get("price"));
  const quantity = toNumber(get("quantity"));
  const productType = String(get("productType") ?? "").trim();
  const retailInitialStockRaw = toNumber(get("retailInitialStock"));
  const storeInitialStockRaw = toNumber(get("storeInitialStock"));
  const retailInitialStock = Number.isFinite(retailInitialStockRaw)
    ? retailInitialStockRaw
    : 0;
  const storeInitialStock = Number.isFinite(storeInitialStockRaw)
    ? storeInitialStockRaw
    : 0;

  if (!productName) errors.push("Product Name is required");
  if (!brand) errors.push("Brand is required");
  if (!category) errors.push("Category is required");
  if (!Number.isFinite(price) || price <= 0) errors.push("Price must be > 0");
  // This field is used as "quantity per unit" for your sample sheet.
  if (!Number.isFinite(quantity) || quantity <= 0)
    errors.push("Quantity per product must be > 0");

  return {
    ok: errors.length === 0,
    errors,
    parsed: {
      productName,
      brand,
      category,
      price,
      quantity,
      productType,
      unit: String(get("unit") ?? "").trim(),
      hsn: String(get("hsn") ?? "").trim(),
      retailInitialStock,
      storeInitialStock,
    },
  };
}


